import api from '../client';

/**
 * Interface for donation request data returned by the API
 */
export interface DonationRequest {
  uuid: string;
  created_at: string;
  leave_hours: number;
  leave_value: number;
  employer_top_up: number;
  total_donation: number;
  charity: {
    uuid: string;
    name: string;
  };
  employer: {
    uuid: string;
    name: string;
  };
  donation_request_status: {
    uuid: string;
    name: string;
  };
  user: {
    uuid: string;
    full_name: string;
    email: string;
  };
}

/**
 * Interface for donation report response
 */
export interface DonationReportResponse {
  success: boolean;
  data?: {
    donation_requests: {
      data: DonationRequest[];
      paging: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from: number;
        to: number;
      };
    };
    filters_applied: Record<string, string>;
    generated_at: string;
  };
  message?: string;
  error?: any;
}

/**
 * Interface for payroll compliance stat object
 */
export interface ComplianceStat {
  value: number;
  change: number;
  change_direction: 'positive' | 'negative';
}

/**
 * Interface for payroll compliance record
 */
export interface ComplianceRecord {
  employer: {
    uuid: string;
    name: string;
  };
  payroll_deductions: number;
  employer_contributions: number;
  compliance_status: 'Compliant' | 'At Risk' | 'Non-Compliant';
  compliance_score: number;
}

/**
 * Interface for payroll compliance report response
 */
export interface PayrollComplianceReportResponse {
  success: boolean;
  data?: {
    stats: {
      total_payroll_deductions: ComplianceStat;
      employer_contributions: ComplianceStat;
      overall_compliance: ComplianceStat;
    };
    records: ComplianceRecord[];
    filters_applied: Record<string, string>;
    generated_at: string;
  };
  message?: string;
  error?: any;
}

/**
 * Interface for report filter parameters
 */
export interface ReportFilters {
  start_date?: string;
  end_date?: string;
  charity_uuid?: string;
  employer_uuid?: string;
  user_uuid?: string;
  donation_request_status_uuid?: string;
  page?: number;
  per_page?: number;
  phrase?: string;
  sort_by?: string;
  sort_direction?: 'asc' | 'desc';
}

/**
 * Export format types
 */
export type ExportFormat = 'pdf' | 'csv' | 'excel';

/**
 * Admin Report Service for generating and exporting reports
 */
export class ReportService {
  private static baseUrl = '/admin/reports';

  /**
   * Generate donation report with optional filters
   * @param filters Optional filters to apply to the report
   * @returns Promise with donation report data
   */
  static async generateDonationReport(filters: ReportFilters = {}): Promise<DonationReportResponse> {
    try {
      const response = await api.get<DonationReportResponse>(`${this.baseUrl}/donation`, { params: filters });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to generate donation report',
        error
      };
    }
  }

  /**
   * Export donation report in specified format
   * @param filters Optional filters to apply to the report
   * @param format Export format (pdf, csv, excel)
   * @returns Promise with blob data for download
   */
  static async exportDonationReport(filters: ReportFilters = {}, format: ExportFormat = 'pdf'): Promise<Blob> {
    try {
      const response = await api.get(`${this.baseUrl}/donation/export`, {
        params: { ...filters, format },
        responseType: 'blob'
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to export donation report');
    }
  }

  /**
   * Generate payroll compliance report with optional filters
   * @param filters Optional filters to apply to the report
   * @returns Promise with payroll compliance report data
   */
  static async generatePayrollComplianceReport(filters: ReportFilters = {}): Promise<PayrollComplianceReportResponse> {
    try {
      const response = await api.get<PayrollComplianceReportResponse>(`${this.baseUrl}/payroll-compliance`, { params: filters });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to generate payroll compliance report',
        error
      };
    }
  }

  /**
   * Export payroll compliance report in specified format
   * @param filters Optional filters to apply to the report
   * @param format Export format (pdf, csv, excel)
   * @returns Promise with blob data for download
   */
  static async exportPayrollComplianceReport(filters: ReportFilters = {}, format: ExportFormat = 'pdf'): Promise<Blob> {
    try {
      const response = await api.get(`${this.baseUrl}/payroll-compliance/export`, {
        params: { ...filters, format },
        responseType: 'blob'
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to export payroll compliance report');
    }
  }

  /**
   * Helper method to download a report blob as a file
   * @param blob The blob data to download
   * @param filename The filename to use for the download
   */
  static downloadReport(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}
